import { trackUsage } from '@creditsights/api-ui-web';
import { RpColumn } from '../models/RpColumn';
import { RiskProductAlert } from '../models/Preference';
import { SearchContentType } from '../hooks/searches/useFetchSearch';
import { DisplayType } from '../pages/Feeds/FeedLeftContainer/DisplayTypeSelection';
import { WIDGET_ID } from '../pages/Feeds/Feed.constants';
import { DocketCase, DocketCaseDocument } from '../models/CompanyDockets';

export const enum TrackUsageEvents {
  RISK_DASHBOARD_MONITOR_MANAGE_COLUMNS_CHECKED = 'RISK_DASHBOARD_MONITOR_MANAGE_COLUMNS_CHECKED',
  RISK_DASHBOARD_MONITOR_MANAGE_COLUMNS_UNCHECKED = 'RISK_DASHBOARD_MONITOR_MANAGE_COLUMNS_UNCHECKED',
  COMPANY_TAB_VIEW = 'COMPANY_TAB_VIEW',
  RISK_PRODUCTS_ALERTS_ENABLED = 'RISK_PRODUCTS_ALERTS_ENABLED',
  RISK_PRODUCTS_ALERTS_DISABLED = 'RISK_PRODUCTS_ALERTS_DISABLED',
  ADD_TO_WATCHLIST = 'ADD_TO_WATCHLIST',
  ISSUERS_COVERAGE_FILTER_SELECTED = 'ISSUERS_COVERAGE_FILTER_SELECTED',
  ISSUERS_COVERAGE_FILTER_REMOVED = 'ISSUERS_COVERAGE_FILTER_REMOVED',
  INTERESTS_ALERT_FREQUENCY = 'INTERESTS_ALERT_FREQUENCY',
  SEARCH_RESULT_COMPANIES = 'SEARCH_RESULT_COMPANIES',
  SEARCH_RESULT_RESEARCH = 'SEARCH_RESULT_RESEARCH',
  SEARCH_RESULT_NEWS = 'SEARCH_RESULT_NEWS',
  SEARCH_RESULT_COVENANTS = 'SEARCH_RESULT_COVENANTS',
  FEEDS_ARTICLE_LIST_VIEW = 'FEEDS_ARTICLE_LIST_VIEW',
  FEEDS_ARTICLE_LIST_PAGE_CHANGE = 'FEEDS_ARTICLE_LIST_PAGE_CHANGE',
  FEEDS_WIDGETS_CUSTOMISATION = 'FEEDS_WIDGETS_CUSTOMISATION',
  DAAG_REPORT_DOWNLOAD = 'DAAG_REPORT_DOWNLOAD',
  COMPANY_PAGE_CS_VIEW_WIDGET_TOOLTIP_OPENED = 'COMPANY_PAGE_CS_VIEW_WIDGET_TOOLTIP_OPENED',
  COMPANY_PAGE_PRINT_INFORMATION = 'COMPANY_PAGE_PRINT_INFORMATION',
  ASK_ANALYST = 'ASK_ANALYST',
  COMPANY_ADDED_TO_WATCHLIST = 'COMPANY_ADDED_TO_WATCHLIST',
  COMPANY_REMOVED_FROM_WATCHLIST = 'COMPANY_REMOVED_FROM_WATCHLIST',
  COMPANY_PAGE_WIDGET_TO_TAB_NAVIGATION = 'COMPANY_PAGE_WIDGET_TO_TAB_NAVIGATION',
  SEARCH_RESULT_FILTER_TYPE = 'SEARCH_RESULT_FILTER_TYPE',
  SEARCH_RESULT_SORT_PREFERNCE = 'SEARCH_RESULT_SORT_PREFERNCE',
  SEARCH_RESULT_SAVE_SEARCH_CREATED = 'SEARCH_RESULT_SAVE_SEARCH_CREATED',
  SEARCH_RESULT_CLICK_ON_SAVED_SEARCH = 'SEARCH_RESULT_CLICK_ON_SAVED_SEARCH',
  SEARCH_RESULT_ARTICLE_PAGE_DEPTH = 'SEARCH_RESULT_ARTICLE_PAGE_DEPTH',
  COMPANY_DOCUMENTS_SUBTAB_VIEW = 'COMPANY_DOCUMENTS_SUBTAB_VIEW',
  COMPANY_DOCUMENTS_DOWNLOAD = 'COMPANY_DOCUMENTS_DOWNLOAD',
  DOCKET_DOCUMENT_REQUEST = 'DOCKET_DOCUMENT_REQUEST',
  DOCKET_DOCUMENT_VIEW = 'DOCKET_DOCUMENT_VIEW',
  RISK_CALCULATOR_OPENED = 'RISK_CALCULATOR_OPENED',
  COMPANY_SNAPSHOT_DOWNLOAD = 'COMPANY_SNAPSHOT_DOWNLOAD',
}

export const trackMonitorManageColumnsUpdate = (columns: RpColumn[], checked: boolean): void => {
  const event = checked
    ? TrackUsageEvents.RISK_DASHBOARD_MONITOR_MANAGE_COLUMNS_CHECKED
    : TrackUsageEvents.RISK_DASHBOARD_MONITOR_MANAGE_COLUMNS_UNCHECKED;

  const trackEventData = [
    {
      key: 'columnGroup',
      value: columns[0].group,
    },
  ];
  if (columns.length === 1) {
    trackEventData.push({
      key: 'column',
      value: columns[0].data_field,
    });
  }

  trackUsage(event, trackEventData);
};

export const trackCompanyTabView = (tabName: string, companyId: string): void => {
  trackUsage(TrackUsageEvents.COMPANY_TAB_VIEW, [
    {
      key: 'tabName',
      value: `${tabName}`,
    },
    {
      key: 'companyId',
      value: `${companyId}`,
    },
  ]);
};

export const trackRiskProductsAlerts = (alert: string, watchlistId: string): void => {
  const event =
    alert === RiskProductAlert.None
      ? TrackUsageEvents.RISK_PRODUCTS_ALERTS_DISABLED
      : TrackUsageEvents.RISK_PRODUCTS_ALERTS_ENABLED;

  const trackUsageData = [
    {
      key: 'watchlistId',
      value: `${watchlistId}`,
    },
  ];

  if (alert !== RiskProductAlert.None) {
    trackUsageData.push({
      key: 'alertFrequency',
      value: `${alert}`,
    });
  }

  trackUsage(event, trackUsageData);
};

export const trackAddToWatchlist = (watchlistId: string): void => {
  trackUsage(TrackUsageEvents.ADD_TO_WATCHLIST, [
    {
      key: 'watchlistId',
      value: `${watchlistId}`,
    },
  ]);
};

export const trackIssuersCoverageFilter = (filter: string, coverage: Array<string>): void => {
  const event =
    coverage.indexOf(filter) === -1
      ? TrackUsageEvents.ISSUERS_COVERAGE_FILTER_REMOVED
      : TrackUsageEvents.ISSUERS_COVERAGE_FILTER_SELECTED;
  trackUsage(event, [
    {
      key: 'coverageFilter',
      value: `${filter}`,
    },
  ]);
};

export const trackSearchResults = (contentType: string, searchPhrase: string, searchResultCount: number): void => {
  let event;
  switch (contentType) {
    case SearchContentType.companies:
      event = TrackUsageEvents.SEARCH_RESULT_COMPANIES;
      break;
    case SearchContentType.covenants:
      event = TrackUsageEvents.SEARCH_RESULT_COVENANTS;
      break;
    case SearchContentType.news:
      event = TrackUsageEvents.SEARCH_RESULT_NEWS;
      break;
    case SearchContentType.research:
      event = TrackUsageEvents.SEARCH_RESULT_RESEARCH;
      break;
    default:
      throw new Error(`${contentType} is not supported`);
  }
  trackUsage(event, [
    {
      key: 'searchPhrase',
      value: `${searchPhrase}`,
    },
    {
      key: 'searchResultCount',
      value: `${searchResultCount}`,
    },
  ]);
};

export const trackFeedsArticleListView = (displayType: DisplayType): void => {
  trackUsage(TrackUsageEvents.FEEDS_ARTICLE_LIST_VIEW, [
    {
      key: 'displayType',
      value: `${displayType}`,
    },
  ]);
};

export const trackFeedsArticleListPagination = (pageNumber: number): void => {
  const searchParams = new URLSearchParams(window.location.search);
  trackUsage(TrackUsageEvents.FEEDS_ARTICLE_LIST_PAGE_CHANGE, [
    {
      key: 'pageNumber',
      value: String(pageNumber),
    },
    {
      key: 'pathName',
      value: `${window.location.pathname}`,
    },
    {
      key: 'pathType',
      value: `${searchParams.get('type')}`,
    },
  ]);
};

export const trackFeedsWidgets = (widgetItems: WIDGET_ID[]): void => {
  const searchParams = new URLSearchParams(window.location.search);
  trackUsage(TrackUsageEvents.FEEDS_WIDGETS_CUSTOMISATION, [
    {
      key: 'pathName',
      value: `${window.location.pathname}`,
    },
    {
      key: 'pathType',
      value: `${searchParams.get('type')}`,
    },
    {
      key: 'widgets',
      value: `${JSON.stringify(widgetItems)}`,
    },
  ]);
};

export const trackDAAGReportDownload = (reportId: number): void => {
  trackUsage(TrackUsageEvents.DAAG_REPORT_DOWNLOAD, [
    {
      key: 'reportId',
      value: String(reportId),
    },
  ]);
};

export const trackResultFilterType = (filterType: string): void => {
  trackUsage(TrackUsageEvents.SEARCH_RESULT_FILTER_TYPE, [
    {
      key: 'filterType',
      value: `${filterType}`,
    },
  ]);
};

export const trackSortPreference = (sortPreference: string): void => {
  trackUsage(TrackUsageEvents.SEARCH_RESULT_SORT_PREFERNCE, [
    {
      key: 'sortType',
      value: `${sortPreference}`,
    },
  ]);
};
export const trackSaveSearch = (saveSearch: string): void => {
  const savedSearchQueryParams = new URLSearchParams(window.location.search);

  trackUsage(TrackUsageEvents.SEARCH_RESULT_SAVE_SEARCH_CREATED, [
    {
      key: 'savedSearchName',
      value: `${saveSearch}`,
    },
    {
      key: 'savedSearchQueryParams',
      value: String(savedSearchQueryParams),
    },
  ]);
};
export const trackOnClickSavedSearches = (savedSearch: string): void => {
  trackUsage(TrackUsageEvents.SEARCH_RESULT_CLICK_ON_SAVED_SEARCH, [
    {
      key: 'savedSearchName',
      value: `${savedSearch}`,
    },
  ]);
};

export const trackSearchArticlesDepth = (articleId: number, contentType: string, pageNumber: boolean): void => {
  trackUsage(TrackUsageEvents.SEARCH_RESULT_ARTICLE_PAGE_DEPTH, [
    {
      key: 'articleId',
      value: `${articleId}`,
    },
    {
      key: 'contentType',
      value: contentType,
    },
    {
      key: 'pageNumber',
      value: String(pageNumber),
    },
    {
      key: 'pathName',
      value: `${window.location.pathname}`,
    },
  ]);
};

const getIssuerIdFromIssuerTag = (issuerTag: string): string => issuerTag.slice(issuerTag.indexOf('/') + 1);

const getIssuerIdFromPathName = (): string => window.location.pathname.split('/')[2];

export const trackCompanyPageCSViewWidgetTooltip = (issuerTag: string): void => {
  trackUsage(TrackUsageEvents.COMPANY_PAGE_CS_VIEW_WIDGET_TOOLTIP_OPENED, [
    {
      key: 'companyId',
      value: `${getIssuerIdFromIssuerTag(issuerTag)}`,
    },
  ]);
};

export const trackCompanyPagePrintInformation = (issuerTag: string, selectedSectionsForPrint: Array<string>): void => {
  trackUsage(TrackUsageEvents.COMPANY_PAGE_PRINT_INFORMATION, [
    {
      key: 'companyId',
      value: `${getIssuerIdFromIssuerTag(issuerTag)}`,
    },
    {
      key: 'selectedSectionsForPrint',
      value: `${selectedSectionsForPrint.join(', ')}`,
    },
  ]);
};

export const trackAskAnalyst = (subject: string): void => {
  trackUsage(TrackUsageEvents.ASK_ANALYST, [
    {
      key: 'subject',
      value: `${subject}`,
    },
    {
      key: 'pathName',
      value: `${window.location.pathname}`,
    },
  ]);
};

export const trackCompanyWatchlistChanges = (watchlistId: string, issuerTag: string, isChecked: boolean): void => {
  const event = isChecked
    ? TrackUsageEvents.COMPANY_REMOVED_FROM_WATCHLIST
    : TrackUsageEvents.COMPANY_ADDED_TO_WATCHLIST;
  trackUsage(event, [
    {
      key: 'watchlistId',
      value: `${watchlistId}`,
    },
    {
      key: 'companyId',
      value: `${getIssuerIdFromIssuerTag(issuerTag)}`,
    },
  ]);
};

export const trackCompanyPageWidgetToTabNavigation = (widgetName: string): void => {
  trackUsage(TrackUsageEvents.COMPANY_PAGE_WIDGET_TO_TAB_NAVIGATION, [
    {
      key: 'companyId',
      value: `${getIssuerIdFromPathName()}`,
    },
    {
      key: 'widget',
      value: `${widgetName}`,
    },
  ]);
};

export const trackCompanyDocumentsTabView = (tabName: string, companyId: string, companyName: string): void => {
  trackUsage(TrackUsageEvents.COMPANY_DOCUMENTS_SUBTAB_VIEW, [
    {
      key: 'tabName',
      value: `${tabName}`,
    },
    {
      key: 'companyId',
      value: `${companyId}`,
    },
    {
      key: 'companyName',
      value: `${companyName}`,
    },
  ]);
};

export const trackDocumentDownload = ({
  tabName,
  documentName,
  documentDate,
  documentType,
  articleId,
}: {
  tabName: string;
  documentName?: string;
  documentDate?: string;
  documentType?: string;
  articleId?: number;
}): void => {
  const trackingData = [
    { key: 'tabName', value: tabName },
    ...(documentName ? [{ key: 'documentName', value: documentName }] : []),
    ...(documentDate ? [{ key: 'documentDate', value: documentDate }] : []),
    ...(documentType ? [{ key: 'documentType', value: documentType }] : []),
    ...(articleId !== undefined ? [{ key: 'articleId', value: articleId.toString() }] : []),
  ];
  trackUsage(TrackUsageEvents.COMPANY_DOCUMENTS_DOWNLOAD, trackingData);
};

export const trackDocketDocumentDownload = (
  companyId: string,
  comapnyName: string,
  docketDocument: DocketCaseDocument,
  docketCase: DocketCase,
  eventType: string
): void => {
  const usageData = [
    {
      key: 'companyId',
      value: `${companyId}`,
    },
    {
      key: 'comapnyName',
      value: `${comapnyName}`,
    },
    {
      key: 'caseId',
      value: `${docketDocument.docketItemCaseId}`,
    },
    {
      key: 'caseName',
      value: `${docketCase.name}`,
    },
    {
      key: 'pacerFormatCaseNumber',
      value: `${docketCase.caseNumber}`,
    },
    {
      key: 'docketItemId',
      value: `${docketDocument.docketItemId}`,
    },
    {
      key: 'docketItemNumber',
      value: `${docketDocument.docketNumber}`,
    },
    {
      key: 'docketItemText',
      value: `${docketDocument.description.substring(0, 149)}`,
    },
  ];
  if (eventType === TrackUsageEvents.DOCKET_DOCUMENT_VIEW) {
    usageData.push({
      key: 'docketItemCost',
      value: `${docketDocument?.docketItemCost ?? 0}`,
    });
  }
  trackUsage(eventType, usageData);
};

export const trackRiskCalculatorClick = (companyId: string, companyName: string, rpSectorModel: string): void => {
  trackUsage(TrackUsageEvents.RISK_CALCULATOR_OPENED, [
    {
      key: 'companyId',
      value: companyId,
    },
    {
      key: 'companyName',
      value: companyName,
    },
    {
      key: 'rpSectorModel',
      value: rpSectorModel,
    },
  ]);
};

export const trackCompanySnapshotDownload = (companyId: string, companyName: string): void => {
  trackUsage(TrackUsageEvents.COMPANY_DOCUMENTS_DOWNLOAD, [
    {
      key: 'companyId',
      value: companyId,
    },
    {
      key: 'companyName',
      value: companyName,
    },
  ]);
};
